import JSZip from 'jszip';

const SETA_COMPRIMENTO_METROS = 35;

function escapeXml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function parseSetasValue(value, posicaoIdx = 0) {
  let parsed = [];
  try {
    if (value) parsed = typeof value === 'string' ? JSON.parse(value) : value;
  } catch (_) {}
  if (!Array.isArray(parsed)) return [];

  if (parsed.length > 0 && Array.isArray(parsed[0])) {
    parsed = parsed[posicaoIdx] || [];
  }

  return parsed.filter(s => s && s.angulo !== undefined && s.angulo !== null);
}

function parseQuantidadeLatLon(value) {
  if (!value) return [];
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function isCoordValida(lat, lng) {
  const la = parseFloat(lat);
  const lo = parseFloat(lng);
  return Number.isFinite(la) && Number.isFinite(lo) && !(la === 0 && lo === 0);
}

/** Desloca coordenada a partir do ângulo cartesiano do SSAT (0° = leste, 90° = norte). */
function offsetLatLng(lat, lng, angleDeg, distanceMeters) {
  const rad = (Number(angleDeg) * Math.PI) / 180;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const dLat = (distanceMeters / 111320) * Math.sin(rad);
  const dLng = (distanceMeters / (111320 * (cosLat || 1))) * Math.cos(rad);
  return {
    lat: lat + dLat,
    lng: lng + dLng,
  };
}

function tipoFaseLabel(tipo) {
  return parseInt(tipo, 10) !== 1 ? 'Travessia de Pedestre' : 'Semafórico';
}

export function collectMapaFaseFeatures(list) {
  const features = [];
  const ordenada = [...(list || [])].sort((a, b) => {
    const na = parseInt(a.numero_controlador, 10) || 0;
    const nb = parseInt(b.numero_controlador, 10) || 0;
    if (na !== nb) return na - nb;
    return (parseInt(a.fase_id, 10) || 0) - (parseInt(b.fase_id, 10) || 0);
  });

  ordenada.forEach(item => {
    const posicoesExtras = parseQuantidadeLatLon(item.quantidade_lat_lon);
    const posicoes = [];

    if (posicoesExtras.length) {
      posicoesExtras.forEach((pos, idx) => {
        if (isCoordValida(pos.latitude, pos.longitude)) {
          posicoes.push({
            lat: parseFloat(pos.latitude),
            lng: parseFloat(pos.longitude),
            posicaoIdx: parseInt(idx, 10) || 0,
          });
        }
      });
    } else if (isCoordValida(item.fase_latitude, item.fase_longitude)) {
      posicoes.push({
        lat: parseFloat(item.fase_latitude),
        lng: parseFloat(item.fase_longitude),
        posicaoIdx: 0,
      });
    }

    posicoes.forEach(pos => {
      features.push({
        lat: pos.lat,
        lng: pos.lng,
        posicaoIdx: pos.posicaoIdx,
        numeroControlador: item.numero_controlador,
        faseId: item.fase_id,
        anel: item.anel,
        endereco: item.controlador_endereco || item.endereco || '',
        tipo: item.tipo,
        setas: parseSetasValue(item.setas, pos.posicaoIdx),
      });
    });
  });

  return features;
}

function buildSetaLinePlacemark(feature, seta, setaIdx) {
  const destino = offsetLatLng(feature.lat, feature.lng, seta.angulo, SETA_COMPRIMENTO_METROS);
  const nome = `Ctrl ${feature.numeroControlador} — Fase ${feature.faseId} — Seta ${setaIdx + 1}`;
  const coords = `${feature.lng},${feature.lat},0 ${destino.lng},${destino.lat},0`;

  return (
    `<Placemark>` +
    `<name>${escapeXml(nome)}</name>` +
    `<description>${escapeXml(`Direção ${seta.angulo}°`)}</description>` +
    `<styleUrl>#setaLine</styleUrl>` +
    `<LineString><tessellate>1</tessellate><coordinates>${coords}</coordinates></LineString>` +
    `</Placemark>`
  );
}

function buildPinPlacemark(feature) {
  const posLabel = feature.posicaoIdx > 0 ? ` (pos. ${feature.posicaoIdx + 1})` : '';
  const nome = `Ctrl ${feature.numeroControlador} — Fase ${feature.faseId}${posLabel}`;
  const descricao = [
    `Controlador: ${feature.numeroControlador}`,
    `Fase: ${feature.faseId}`,
    feature.anel != null ? `Anel: ${feature.anel}` : null,
    `Tipo: ${tipoFaseLabel(feature.tipo)}`,
    feature.endereco ? `Endereço: ${feature.endereco}` : null,
    `Latitude: ${feature.lat}`,
    `Longitude: ${feature.lng}`,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    `<Placemark>` +
    `<name>${escapeXml(nome)}</name>` +
    `<description>${escapeXml(descricao)}</description>` +
    `<styleUrl>#pin</styleUrl>` +
    `<Point><coordinates>${feature.lng},${feature.lat},0</coordinates></Point>` +
    `</Placemark>`
  );
}

export function buildMapaFaseKml(features, options = {}) {
  const grupoId = options.grupoId != null ? options.grupoId : '';
  const titulo = options.titulo || `Mapa de Fases — Grupo ${grupoId}`;
  const exportadoEm = options.exportadoEm || new Date().toISOString();

  const placemarks = [];
  features.forEach(feature => {
    placemarks.push(buildPinPlacemark(feature));
    feature.setas.forEach((seta, idx) => {
      placemarks.push(buildSetaLinePlacemark(feature, seta, idx));
    });
  });

  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<kml xmlns="http://www.opengis.net/kml/2.2">` +
    `<Document>` +
    `<name>${escapeXml(titulo)}</name>` +
    `<description>${escapeXml(`Exportado em ${exportadoEm}. Posições de fases e direções das setas.`)}</description>` +
    `<Style id="pin">` +
    `<IconStyle><scale>1.0</scale><Icon><href>http://maps.google.com/mapfiles/kml/paddle/blu-blank.png</href></Icon></IconStyle>` +
    `</Style>` +
    `<Style id="setaLine">` +
    `<LineStyle><color>ffeb6325</color><width>3</width></LineStyle>` +
    `</Style>` +
    placemarks.join('') +
    `</Document>` +
    `</kml>`
  );
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function buildFilename(grupoId) {
  const data = new Date();
  const yyyy = data.getFullYear();
  const mm = String(data.getMonth() + 1).padStart(2, '0');
  const dd = String(data.getDate()).padStart(2, '0');
  const grupo = grupoId != null && grupoId !== '' ? `grupo-${grupoId}` : 'grupo';
  return `mapa-fases-${grupo}-${yyyy}${mm}${dd}.kmz`;
}

export async function downloadMapaFaseKmz(list, options = {}) {
  const features = collectMapaFaseFeatures(list);
  if (!features.length) {
    throw new Error('Nenhuma fase com coordenadas para exportar.');
  }

  const kml = buildMapaFaseKml(features, options);
  const zip = new JSZip();
  zip.file('doc.kml', kml);
  const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.google-earth.kmz' });
  triggerDownload(blob, buildFilename(options.grupoId));
  return { totalFeatures: features.length };
}
